import { Icon } from "@iconify/react";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import { getPlatformIcon } from "../../utils/websiteHelpers";

const WordPressConnectionCard = ({
  connection,
  selectedPage,
  onTestConnection,
  onShowSiteInfo,
  onShowScanHistory,
  onScanPageAccessibility,
  onRollbackFixes,
  onGetViolations,
  onDisconnectSite,
  isTestingConnection,
  isGettingSiteInfo,
  isGettingPages,
  isGettingScanHistory,
  isScanningPageAccessibility,
  isRollingBack,
  isGettingRollbackInfo,
  isGettingViolations,
  isDisconnectingSite,
  shopifyExtensionStatus,
  isCheckingShopifyExtension,
  onEnableShopifyExtension,
}) => {
  if (!connection) return null;

  const isShopify = connection.platform === "shopify";
  const isShopifyExtensionDisabled =
    isShopify && shopifyExtensionStatus && !shopifyExtensionStatus.enabled;

  const isAnyLoading =
    isTestingConnection ||
    isGettingSiteInfo ||
    isGettingPages ||
    isGettingScanHistory ||
    isScanningPageAccessibility ||
    isRollingBack ||
    isGettingRollbackInfo ||
    isGettingViolations ||
    isDisconnectingSite;

  return (
    <div
      className={`p-6 bg-gradient-to-r from-indigo-600 to-primary text-white rounded-lg transition-all duration-300 ${isAnyLoading ? "opacity-75 pointer-events-none" : ""}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Icon
            icon={getPlatformIcon(connection.platform)}
            className="text-5xl"
          />
          <div>
            <h3 className="text-xl font-semibold">{connection.siteName}</h3>
            <p className="text-sm opacity-90">{connection.siteUrl}</p>
          </div>
        </div>
      </div>

      {/* Action Buttons Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2 mb-4">
        <Button
          variant="secondary"
          size="sm"
          icon="mdi:connection"
          onClick={() => onTestConnection(connection._id || connection.id)}
          disabled={isTestingConnection || connection.platform !== "wordpress"}
          className="bg-white/20 hover:bg-white/30 border-white/30 text-white"
        >
          {isTestingConnection ? "Testing..." : "Test"}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon="mdi:information"
          onClick={onShowSiteInfo}
          disabled={isGettingSiteInfo || connection.platform !== "wordpress"}
          className="bg-white/20 hover:bg-white/30 border-white/30 text-white"
        >
          {isGettingSiteInfo ? "Loading..." : "Site Info"}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon="mdi:search-web"
          onClick={onScanPageAccessibility}
          disabled={isScanningPageAccessibility || !selectedPage}
          className="bg-white/20 hover:bg-white/30 border-white/30 text-white"
          title={!selectedPage ? "Please select a page first" : ""}
        >
          {isScanningPageAccessibility ? "Scanning..." : "Scan Page"}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon="mdi:history"
          onClick={onShowScanHistory}
          disabled={isGettingScanHistory}
          className="bg-white/20 hover:bg-white/30 border-white/30 text-white"
        >
          {isGettingScanHistory ? "Loading..." : "History"}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon="mdi:undo"
          onClick={onRollbackFixes}
          disabled={!selectedPage || isRollingBack || isGettingRollbackInfo || connection.platform !== "wordpress"}
          className="bg-white/20 hover:bg-white/30 border-white/30 text-white"
          title={!selectedPage ? "Please select a page first" : ""}
        >
          {isGettingRollbackInfo ? "Loading..." : isRollingBack ? "Rolling..." : "Rollback"}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon="mdi:alert-circle"
          onClick={onGetViolations}
          disabled={isGettingViolations || connection.platform !== "wordpress"}
          className="bg-white/20 hover:bg-white/30 border-white/30 text-white"
        >
          {isGettingViolations ? "Loading..." : "Violations"}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon="mdi:link-off"
          onClick={onDisconnectSite}
          disabled={isDisconnectingSite || connection.platform !== "wordpress"}
          className="bg-white/20 hover:bg-white/30 border-white/30 text-white"
        >
          {isDisconnectingSite ? "Disconnecting..." : "Disconnect"}
        </Button>
        {isShopify && isCheckingShopifyExtension && (
          <Button
            variant="secondary"
            size="sm"
            disabled
            className="bg-white/20 border-white/30 text-white"
          >
            <LoadingSpinner size="xs" />
            <span className="ml-1.5">Checking...</span>
          </Button>
        )}
        {isShopifyExtensionDisabled && (
          <Button
            variant="secondary"
            size="sm"
            icon="logos:shopify"
            onClick={onEnableShopifyExtension}
            className="bg-white/20 hover:bg-white/30 border-white/30 text-white"
          >
            Enable Theme Extension
          </Button>
        )}
        {isShopify &&
          !isCheckingShopifyExtension &&
          shopifyExtensionStatus?.enabled && (
            <Button
              variant="secondary"
              size="sm"
              icon="mdi:check-circle"
              disabled
              className="bg-white/20 border-white/30 text-white"
            >
              AI Fixes Enabled
            </Button>
          )}
      </div>
      <div className="flex flex-wrap gap-8 pt-4 border-t border-white/20">
        <div>
          <div className="text-xs opacity-80 mb-1">Platform</div>
          <div className="font-semibold flex items-center gap-1">
            <Icon icon={getPlatformIcon(connection.platform)} />
            {connection.platform}
          </div>
        </div>
        <div>
          <div className="text-xs opacity-80 mb-1">Status</div>
          <div className="font-semibold flex items-center gap-1">
            <Icon
              icon={
                connection.isActive ? "mdi:check-circle" : "mdi:close-circle"
              }
            />
            {connection.isActive ? "Active" : "Inactive"}
          </div>
        </div>
        {connection.connectionStatus && (
          <div>
            <div className="text-xs opacity-80 mb-1">Connection</div>
            <div className="font-semibold flex items-center gap-1 capitalize">
              <Icon
                icon={
                  connection.connectionStatus === "connected"
                    ? "mdi:lan-connect"
                    : "mdi:lan-disconnect"
                }
                className={
                  connection.connectionStatus === "connected"
                    ? "text-green-300"
                    : "text-red-300"
                }
              />
              {connection.connectionStatus}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WordPressConnectionCard;
